import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/safeClient';
import { useAuth } from './AuthContext';
import { isUuid } from '@/lib/validation';
import {
  LIGHT_COLUMNS,
  PROFILE_COLUMNS,
  type ListingLight,
  type Profile
} from '@/services/listings';

// Re-export for backward compatibility
export type { Profile, ListingLight };
export type Listing = ListingLight;

interface RealtimeState {
  listings: Map<string, ListingLight>;
  likes: Map<string, number>;
  profiles: Map<string, Profile>;
  isReady: boolean;
  pendingLikeDelta: Map<string, number>;
  ignoreNextLikeEvent: Map<string, number>;
}

interface RealtimeContextValue extends RealtimeState {
  refreshListing: (id: string) => Promise<void>;
  refreshProfile: (id: string) => Promise<void>;
  getLikeCount: (listingId: string, fallback?: number) => number;
  adjustLikeCount: (listingId: string, delta: 1 | -1) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Per-ID request counters to discard stale refresh responses
  const refreshListingIds = useRef(new Map<string, number>());
  const refreshProfileIds = useRef(new Map<string, number>());
  const [state, setState] = useState<RealtimeState>({
    listings: new Map(),
    likes: new Map(),
    profiles: new Map(),
    isReady: false,
    pendingLikeDelta: new Map(),
    ignoreNextLikeEvent: new Map(),
  });

  // OPTIMIZED: No initial bulk fetch — listings are loaded via useListingsPaginated.
  // RealtimeContext only manages real-time subscriptions and on-demand refreshes.
  useEffect(() => {
    setState((prev) => ({ ...prev, isReady: true }));
  }, [user?.id]);

  // Real-time subscriptions
  useEffect(() => {
    if (!state.isReady) return;

    // 1️⃣ Listings channel
    const listingsChannel = supabase
      .channel('realtime:listings')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'listings',
        },
        async (payload) => {
          const newListing = payload.new as any;
          
          // Skip fetching profile if seller_id is not a valid UUID (e.g., demo listings)
          if (!newListing.seller_id || !isUuid(newListing.seller_id)) {
            return;
          }
          
          // Fetch seller profile
          const { data: seller } = await (supabase as any)
            .from('profiles_public')
            .select('id, name, username, avatar_url, county, verified, trust_score')
            .eq('id', newListing.seller_id)
            .single();

          if (seller) {
            setState((prev) => {
              const newListings = new Map(prev.listings);
              const newProfiles = new Map(prev.profiles);
              const newLikes = new Map(prev.likes);
              const seededLikes = Math.max(0, Number(newListing.likes ?? 0));
              
              newListings.set(newListing.id, { ...newListing, seller });
              newProfiles.set(seller.id, seller);
              newLikes.set(newListing.id, seededLikes);
              
              return { ...prev, listings: newListings, profiles: newProfiles, likes: newLikes };
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'listings',
        },
        (payload) => {
          const updatedListing = payload.new as any;
          
          setState((prev) => {
            const newListings = new Map(prev.listings);
            const existing = newListings.get(updatedListing.id);
            
            if (existing) {
              newListings.set(updatedListing.id, {
                ...existing,
                ...updatedListing,
                seller: existing.seller, // Keep existing seller
              });
            }

            // If the server sent an updated likes count, treat it as authoritative:
            // seed the likes base and clear any pending optimistic delta
            const newLikes = new Map(prev.likes);
            const newPending = new Map(prev.pendingLikeDelta);
            const newIgnore = new Map(prev.ignoreNextLikeEvent);
            if (updatedListing.likes != null) {
              newLikes.set(updatedListing.id, updatedListing.likes);
              newPending.delete(updatedListing.id);
            }
            
            return { 
              ...prev, 
              listings: newListings,
              likes: newLikes,
              pendingLikeDelta: newPending,
              ignoreNextLikeEvent: newIgnore,
            };
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'listings',
        },
        (payload) => {
          const deletedId = payload.old.id;
          
          setState((prev) => {
            const newListings = new Map(prev.listings);
            const newLikes = new Map(prev.likes);
            
            newListings.delete(deletedId);
            newLikes.delete(deletedId);
            
            return { ...prev, listings: newListings, likes: newLikes };
          });
        }
      )
      .subscribe();

    // 2️⃣ Likes channel - with optimistic update handling
    const likesChannel = supabase
      .channel('realtime:likes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'likes',
          filter: 'target_type=eq.listing',
        },
        (payload) => {
          const targetId = payload.new.target_id;
          
          setState((prev) => {
            const newLikes = new Map(prev.likes);
            const newIgnore = new Map(prev.ignoreNextLikeEvent);
            const newPending = new Map(prev.pendingLikeDelta);
            
            // Seed base from listing data if not yet in likes map
            const seedBase = () => {
              if (!newLikes.has(targetId)) {
                const listingLikes = prev.listings.get(targetId)?.likes ?? 0;
                newLikes.set(targetId, listingLikes);
              }
            };

            // Check if we should ignore this event (it's our optimistic update)
            const ignoreCount = newIgnore.get(targetId) || 0;
            if (ignoreCount > 0) {
              if (ignoreCount <= 1) {
                newIgnore.delete(targetId);
              } else {
                newIgnore.set(targetId, ignoreCount - 1);
              }

              const pendingDelta = newPending.get(targetId) || 0;
              if (pendingDelta > 0) {
                if (pendingDelta === 1) {
                  newPending.delete(targetId);
                } else {
                  newPending.set(targetId, pendingDelta - 1);
                }
              }

              return {
                ...prev,
                ignoreNextLikeEvent: newIgnore,
                pendingLikeDelta: newPending,
              };
            }
            
            // This is from another user, update the base count
            seedBase();
            const currentCount = newLikes.get(targetId) || 0;
            const newCount = currentCount + 1;
            newLikes.set(targetId, newCount);
            
            return { ...prev, likes: newLikes };
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'likes',
          filter: 'target_type=eq.listing',
        },
        (payload) => {
          const targetId = payload.old.target_id;
          
          setState((prev) => {
            const newLikes = new Map(prev.likes);
            const newIgnore = new Map(prev.ignoreNextLikeEvent);
            const newPending = new Map(prev.pendingLikeDelta);
            
            // Seed base from listing data if not yet in likes map
            const seedBase = () => {
              if (!newLikes.has(targetId)) {
                const listingLikes = prev.listings.get(targetId)?.likes ?? 0;
                newLikes.set(targetId, listingLikes);
              }
            };

            // Check if we should ignore this event (it's our optimistic update)
            const ignoreCount = newIgnore.get(targetId) || 0;
            if (ignoreCount > 0) {
              if (ignoreCount <= 1) {
                newIgnore.delete(targetId);
              } else {
                newIgnore.set(targetId, ignoreCount - 1);
              }

              const pendingDelta = newPending.get(targetId) || 0;
              if (pendingDelta < 0) {
                if (pendingDelta === -1) {
                  newPending.delete(targetId);
                } else {
                  newPending.set(targetId, pendingDelta + 1);
                }
              }

              return {
                ...prev,
                ignoreNextLikeEvent: newIgnore,
                pendingLikeDelta: newPending,
              };
            }
            
            // This is from another user, update the base count
            seedBase();
            const currentCount = newLikes.get(targetId) || 1;
            const newCount = Math.max(0, currentCount - 1);
            newLikes.set(targetId, newCount);
            
            return { ...prev, likes: newLikes };
          });
        }
      )
      .subscribe();

    // 3️⃣ Profiles channel - 최적화: 연결된 listings만 selective update
    const profilesChannel = supabase
      .channel('realtime:profiles')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
        },
        (payload) => {
          const updatedProfile = payload.new as Profile;
          
          setState((prev) => {
            const newProfiles = new Map(prev.profiles);
            const newListings = new Map(prev.listings);
            
            // Update profile
            newProfiles.set(updatedProfile.id, updatedProfile);
            
            // Update all listings from this seller (selective update)
            for (const [listingId, listing] of newListings.entries()) {
              if (listing.seller_id === updatedProfile.id) {
                newListings.set(listingId, {
                  ...listing,
                  seller: updatedProfile,
                });
              }
            }
            
            return { ...prev, profiles: newProfiles, listings: newListings };
          });
        }
      )
      .subscribe();

    // 3️⃣ Cleanup 시 removeAllChannels() 호출
    return () => {
      supabase.removeChannel(listingsChannel);
      supabase.removeChannel(likesChannel);
      supabase.removeChannel(profilesChannel);
    };
  }, [state.isReady]);

  // Helper functions - OPTIMIZED: use lightweight columns
  const refreshListing = async (id: string) => {
    // Stamp this request; discard response if a newer refresh for same id arrives first
    const requestId = (refreshListingIds.current.get(id) ?? 0) + 1;
    refreshListingIds.current.set(id, requestId);

    const { data } = await supabase
      .from('listings')
      .select(`
        ${LIGHT_COLUMNS},
        seller:profiles!seller_id(${PROFILE_COLUMNS})
      `)
      .eq('id', id)
      .single();

    // Stale check: a newer refresh for this id already completed or started
    if (refreshListingIds.current.get(id) !== requestId) return;

    if (data) {
      setState((prev) => {
        const newListings = new Map(prev.listings);
        const newLikes = new Map(prev.likes);
        const seededLikes = Math.max(0, Number(data.likes ?? 0));

        newListings.set(id, data as ListingLight);
        newLikes.set(id, seededLikes);

        if (data.seller) {
          const newProfiles = new Map(prev.profiles);
          newProfiles.set(data.seller.id, data.seller as Profile);
          return { ...prev, listings: newListings, profiles: newProfiles, likes: newLikes };
        }

        return { ...prev, listings: newListings, likes: newLikes };
      });
    }
  };

  const refreshProfile = async (id: string) => {
    // Skip if not a valid UUID
    if (!id || !isUuid(id)) {
      return;
    }

    // Stamp this request; discard response if a newer refresh for same id arrives first
    const requestId = (refreshProfileIds.current.get(id) ?? 0) + 1;
    refreshProfileIds.current.set(id, requestId);

    const { data } = await (supabase as any)
      .from('profiles_public')
      .select('id, name, avatar_url, county, verified, trust_score')
      .eq('id', id)
      .single();

    // Stale check
    if (refreshProfileIds.current.get(id) !== requestId) return;

    if (data) {
      setState((prev) => {
        const newProfiles = new Map(prev.profiles);
        newProfiles.set(id, data as Profile);
        return { ...prev, profiles: newProfiles };
      });
    }
  };

  // Get current like count (base + pending delta)
  // Falls back to listing's own likes column when no realtime base exists,
  // then to caller-supplied fallback (e.g. listing.likes from paginated data)
  const getLikeCount = (listingId: string, fallback?: number): number => {
    const realtimeBase = state.likes.get(listingId);
    const listingBase = state.listings.get(listingId)?.likes;
    const baseLikes = realtimeBase ?? listingBase ?? fallback ?? 0;
    const pendingDelta = state.pendingLikeDelta.get(listingId) || 0;
    const total = Math.max(0, baseLikes + pendingDelta);
    return total;
  };

  // Adjust like count optimistically
  const adjustLikeCount = (listingId: string, delta: 1 | -1) => {
    setState((prev) => {
      const newPending = new Map(prev.pendingLikeDelta);
      const newIgnore = new Map(prev.ignoreNextLikeEvent);
      
      const currentPending = newPending.get(listingId) || 0;
      newPending.set(listingId, currentPending + delta);
      
      const currentIgnore = newIgnore.get(listingId) || 0;
      newIgnore.set(listingId, currentIgnore + 1);
      
      return {
        ...prev,
        pendingLikeDelta: newPending,
        ignoreNextLikeEvent: newIgnore,
      };
    });
  };

  return (
    <RealtimeContext.Provider 
      value={{
        ...state,
        refreshListing,
        refreshProfile,
        getLikeCount,
        adjustLikeCount,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within RealtimeProvider');
  }
  return context;
}
